import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { toastAxiosError } from "components/Utils";
import { saveAs } from "file-saver";
import { AppThunk } from "../../app/store";
import { Applicant, RelationshipType } from "../Entity/Applicant";
import { GetAppFormUsersInfoRep, getApplicantFileRep, getApplicantFormDtoRep, getEmergencyContactRelationshipTypesRep } from "../Repository/ApplicantRepository";

interface ApplicantState {
  isRequesting: boolean,
  isRequestSuccess: boolean,
  isRequestFailed: boolean,
  error: AxiosError | null,
  applicantFormDto: Applicant|null,
  isRequestingAppFormUsersInfo: boolean,
  isSuccessAppFormUsersInfo: boolean,
  isFailedAppFormUsersInfo: boolean,
  appFormUsersInfo: Applicant[],
  isRequestingEmergencyContactRelTypes: boolean,
  emergencyContactRelTypes: RelationshipType[]
}

const initialState: ApplicantState = {
  isRequesting: false,
  isRequestSuccess: false,
  isRequestFailed: false,
  error: null,
  applicantFormDto: null,
  isRequestingAppFormUsersInfo: false,
  isSuccessAppFormUsersInfo: false,
  isFailedAppFormUsersInfo: false,
  appFormUsersInfo: [],
  isRequestingEmergencyContactRelTypes: false,
  emergencyContactRelTypes: []
}

function startRequestRdc(state: ApplicantState) {
  state.isRequesting = true;
  state.isRequestFailed = false;
  state.error = null;
}

function requestFailedRdc(state: ApplicantState, action: PayloadAction<AxiosError>) {
  state.isRequesting = false;
  state.isRequestSuccess = false;
  state.isRequestFailed = true;
  state.error = action.payload;
}

function requestDtoSuccessRdc(state: ApplicantState, action: PayloadAction<Applicant>) {
  state.isRequesting = false;
  state.isRequestSuccess = true;
  state.isRequestFailed = false;
  state.error = null;
  state.applicantFormDto = action.payload;
}


function startGetAppFormUsersInfoRdc(state: ApplicantState) {
  state.isRequestingAppFormUsersInfo = true;
  state.isSuccessAppFormUsersInfo = false;
}
function GetAppFormUsersInfoSuccessRdc(state: ApplicantState, action: PayloadAction<Applicant[]>) {
  state.isRequestingAppFormUsersInfo = false;
  state.isSuccessAppFormUsersInfo = true;
  state.appFormUsersInfo = action.payload;
}

function GetAppFormUsersInfoFailedRdc(state: ApplicantState, action: PayloadAction<AxiosError>) {
  state.isRequestingAppFormUsersInfo = false;
  state.isFailedAppFormUsersInfo = true;
  state.error = action.payload;
}

function startGetEmergencyContactRelTypesRdc(state: ApplicantState) {
  state.isRequestingEmergencyContactRelTypes = true;
  state.emergencyContactRelTypes = [];
}
function getEmergencyContactRelTypesSuccessRdc(state: ApplicantState, action: PayloadAction<RelationshipType[]>) {
  state.isRequestingEmergencyContactRelTypes = false;
  state.emergencyContactRelTypes = action.payload;
}

const ApplicantSlice = createSlice({
    name: 'Applicant',
    initialState: initialState,
    reducers: {
      startRequestAct: startRequestRdc,
      requestDtoSuccessAct: requestDtoSuccessRdc,
      requestFailedAct: requestFailedRdc,
      startGetAppFormUsersInfoAct: startGetAppFormUsersInfoRdc,
      GetAppFormUsersInfoSuccessAct: GetAppFormUsersInfoSuccessRdc,
      GetAppFormUsersInfoFailedAct: GetAppFormUsersInfoFailedRdc,
      startGetEmergencyContactRelTypesAct: startGetEmergencyContactRelTypesRdc,
      getEmergencyContactRelTypesSuccessAct: getEmergencyContactRelTypesSuccessRdc
    }
  }
)

export const {
  startRequestAct,
  requestDtoSuccessAct,
  requestFailedAct,
  startGetAppFormUsersInfoAct,
  GetAppFormUsersInfoSuccessAct,
  GetAppFormUsersInfoFailedAct,
  startGetEmergencyContactRelTypesAct,
  getEmergencyContactRelTypesSuccessAct
} = ApplicantSlice.actions;

export default ApplicantSlice.reducer;

export const getApplicantFormDto = (applicationFormUid: string, applicationFormSectionId: number): AppThunk => async dispatch =>
{
    try
    {
        dispatch(startRequestAct());
        const apiResponse = await getApplicantFormDtoRep(applicationFormUid, applicationFormSectionId);
        dispatch(requestDtoSuccessAct(apiResponse));
    } catch (err)
    {
        dispatch(requestFailedAct(err as AxiosError));
    }
}

export const downloadApplicantFile = (applicationFormUid: string, filename: string, filetype: string): AppThunk => async dispatch =>
{
    try
    {
        const apiResponse = await getApplicantFileRep(applicationFormUid, filetype);

        saveAs(apiResponse, filename);
    } catch (err)
    {
        toastAxiosError(err as AxiosError);
        dispatch(requestFailedAct(err as AxiosError));
    }
}

export const GetAppFormUsersInfo = (applicationFormUid: string): AppThunk => async dispatch => {
  try {
    dispatch(startGetAppFormUsersInfoAct());
    const applicants = await GetAppFormUsersInfoRep(applicationFormUid);
    dispatch(GetAppFormUsersInfoSuccessAct(applicants));
  } catch (err) {
    toastAxiosError(err as AxiosError);
    dispatch(GetAppFormUsersInfoFailedAct(err as AxiosError));
  }
}

export const getEmergencyContactRelTypes = (): AppThunk => async dispatch => {
  try {
    dispatch(startGetEmergencyContactRelTypesAct());
    const relTypes = await getEmergencyContactRelationshipTypesRep();
    dispatch(getEmergencyContactRelTypesSuccessAct(relTypes));
  } catch (err) {
    toastAxiosError(err as AxiosError);
    dispatch(GetAppFormUsersInfoFailedAct(err as AxiosError));
  }
}
